/**
 * Admin V3 — Partners — Table component
 *
 * 10 columns: (expand) | Partenaire | Type | Statut | Solde courant
 *           | Limite crédit | Utilisation | Téléphone | Créé le | Actions.
 *
 * Per-row expandable detail panel shows: address, pricing_profile_id,
 * billing_profile_id, tenant_id. The api_key field is DELIBERATELY OMITTED
 * from display even though the backend returns it (defense-in-depth).
 *
 * Row Actions (AV3 A-7): RBAC-gated Activate/Suspend lifecycle toggle.
 *   (Partner create/edit are NOT surfaced here — the canonical create endpoint
 *   entangles a partner credential + billing profile, and the edit endpoint is
 *   query-parameter-shaped; both are out of the A-7 guardrails. See A-7 report.)
 *
 * Suspended rows: rose left-border for visual scan.
 */

import React, { useState } from 'react';
import { Building2, AlertCircle, RotateCcw, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PartnerStatusToggle from './PartnerStatusToggle';
import {
  cn,
  currency,
  EmptyState,
  SmallPill,
  partnerStatusMeta,
  partnerTypeMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(10).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const CreditUsageBar = ({ balance, limit }) => {
  const safeLimit = Number(limit) || 0;
  const safeBalance = Math.max(0, Number(balance) || 0);
  const pct = safeLimit > 0 ? Math.min(100, (safeBalance / safeLimit) * 100) : 0;
  const tone =
    pct >= 90 ? 'bg-rose-500' :
    pct >= 75 ? 'bg-amber-500' :
                'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden min-w-[60px]">
        <div className={cn('h-full', tone)} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] text-slate-500 tabular-nums w-9 text-right">
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

const DetailPanel = ({ partner, locale }) => {
  // SECURITY: `api_key` is deliberately omitted from display even if present
  // in the payload. Read-only-side defense-in-depth.
  const rows = [
    partner.address              && [t('partners.detail.address',         locale), partner.address],
    partner.pricing_profile_id   && [t('partners.detail.pricingProfile',  locale), partner.pricing_profile_id],
    partner.billing_profile_id   && [t('partners.detail.billingProfile',  locale), partner.billing_profile_id],
    partner.tenant_id            && [t('partners.detail.tenant',          locale), partner.tenant_id],
  ].filter(Boolean);

  if (rows.length === 0) {
    return <div className="text-[11px] italic text-slate-400 px-2 py-1">—</div>;
  }

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
            <dd className="text-slate-700 font-mono break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const PartnersListTable = ({ partners, loading, error, isFilterActive, onRetry, onRefresh, locale = 'fr' }) => {
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-partners-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('partners.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-partners-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('partners.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Building2}
      title={t('partners.empty.title', locale)}
      desc={t(isFilterActive ? 'partners.empty.descFilter' : 'partners.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('partners.col.partner',     locale),
                t('partners.col.type',        locale),
                t('partners.col.status',      locale),
                t('partners.col.balance',     locale),
                t('partners.col.creditLimit', locale),
                t('partners.col.creditUsage', locale),
                t('partners.col.phone',       locale),
                t('partners.col.created',     locale),
                t('partners.col.actions',     locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : partners.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              partners.map((p) => {
                const key = p.id;
                const isExpanded = Boolean(expanded[key]);
                const typeMeta = partnerTypeMeta[p.type] || { label: p.type || '—', pill: 'bg-slate-100 text-slate-500 border border-slate-200' };
                const statusMeta = partnerStatusMeta[p.status] || partnerStatusMeta.pending;
                const isSuspended = p.status === 'suspended';
                const balance = Number(p.current_balance) || 0;
                const creditLimit = Number(p.credit_limit) || 0;

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isSuspended && 'bg-rose-50/30'
                      )}
                      style={isSuspended ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded ? t('partners.detail.hide', locale) : t('partners.detail.show', locale)}
                          data-testid={`adminv3-partners-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Partenaire */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-violet-300 flex-shrink-0">
                            {initialsOf(p.name)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{p.name || '—'}</div>
                            <div className="text-[11px] text-slate-400 truncate">{p.contact_email || ''}</div>
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={typeMeta} />
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>

                      {/* Solde courant */}
                      <td className={cn(
                        'px-4 py-3.5 font-medium whitespace-nowrap',
                        balance > 0 ? 'text-slate-900' : 'text-slate-400'
                      )}>
                        {currency(balance)}
                      </td>

                      {/* Limite crédit */}
                      <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap">
                        {currency(creditLimit)}
                      </td>

                      {/* Utilisation */}
                      <td className="px-4 py-3.5 min-w-[110px]">
                        <CreditUsageBar balance={balance} limit={creditLimit} />
                      </td>

                      {/* Téléphone */}
                      <td className="px-4 py-3.5 text-slate-700 font-mono text-[12px]">
                        {p.contact_phone || '—'}
                      </td>

                      {/* Créé le */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatDate(p.created_at, locale)}
                      </td>

                      {/* Actions (RBAC-gated lifecycle) */}
                      <td className="px-4 py-3.5">
                        <PartnerStatusToggle partner={p} onDone={onRefresh} locale={locale} />
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={10} className="px-4 py-2">
                          <DetailPanel partner={p} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default PartnersListTable;
